// Google tag (Google Ads + optional GA4) for the public website.
// Nothing loads unless the IDs are set as Vercel environment variables, so builds without them
// behave exactly as before:
//   REACT_APP_GOOGLE_ADS_ID             AW-XXXXXXXXX (Google Ads → Goals → Conversions → tag setup)
//   REACT_APP_GOOGLE_ADS_BOOKING_LABEL  label of the "Booking paid" conversion (the part after the slash)
//   REACT_APP_GOOGLE_ADS_CALL_LABEL     label of the "Phone call click" conversion (optional)
//   REACT_APP_GA4_ID                    G-XXXXXXXXXX (optional)
// The admin panel and rider account pages are never tracked.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || '';
const BOOKING_LABEL = process.env.REACT_APP_GOOGLE_ADS_BOOKING_LABEL || '';
const CALL_LABEL = process.env.REACT_APP_GOOGLE_ADS_CALL_LABEL || '';
const GA4_ID = process.env.REACT_APP_GA4_ID || '';
const PRIMARY_ID = ADS_ID || GA4_ID;
const TRACKED_KEY = 'rooldrive:trackedBookings';

const isPrivatePath = (path) => /^\/(admin|dashboard|design)(\/|$)/.test(path);

let loaded = false;

function gtag() {
  // gtag.js reads the arguments object, so this must stay a plain function
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

function load() {
  // scripts/prerender.js sets __ROOLDRIVE_PRERENDER__ so build-time renders never load or send analytics
  if (loaded || !PRIMARY_ID || typeof window === 'undefined' || window.__ROOLDRIVE_PRERENDER__) return;
  loaded = true;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(PRIMARY_ID)}`;
  document.head.appendChild(script);
  window.gtag = gtag;
  gtag('js', new Date());
  // Page views are sent by usePageTracking on every route change, so not here.
  if (ADS_ID) gtag('config', ADS_ID, { send_page_view: false });
  if (GA4_ID) gtag('config', GA4_ID, { send_page_view: false });
  document.addEventListener('click', onDocumentClick, true);
}

function send(...args) {
  if (!loaded) return;
  gtag(...args);
}

// Every phone link on the site counts as a call lead; store links count as app interest.
function onDocumentClick(event) {
  const link = event.target.closest && event.target.closest('a[href]');
  if (!link || isPrivatePath(window.location.pathname)) return;
  const href = link.getAttribute('href') || '';
  if (href.startsWith('tel:')) {
    send('event', 'phone_call_click', { link_url: href, page_path: window.location.pathname });
    if (ADS_ID && CALL_LABEL) send('event', 'conversion', { send_to: `${ADS_ID}/${CALL_LABEL}` });
  } else if (/apps\.apple\.com|play\.google\.com/.test(href)) {
    send('event', 'app_store_click', { link_url: href, store: href.includes('apple') ? 'ios' : 'android' });
  }
}

// Mount once inside the Router. Loads the tag on public pages and sends a page view per route.
export function usePageTracking() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (isPrivatePath(pathname)) return;
    load();
    send('event', 'page_view', { page_path: pathname, page_location: window.location.href, page_title: document.title });
  }, [pathname]);
}

export function trackBeginBooking(serviceName) {
  send('event', 'begin_checkout', { item_category: serviceName || undefined });
}

// Booking funnel steps (add_shipping_info = prices shown, add_payment_info = payment sheet opened).
// view_cart = review step opened, pay_click = Book & pay tapped, review_blocked = { reason }.
// Only the service name (and a fixed reason code): never addresses, names, emails or phone numbers.
export function trackEvent(name, serviceName, { reason } = {}) {
  send('event', name, { item_category: serviceName || undefined, reason: reason || undefined });
}

function alreadyTracked(id) {
  try {
    const seen = JSON.parse(localStorage.getItem(TRACKED_KEY) || '[]');
    if (seen.includes(id)) return true;
    localStorage.setItem(TRACKED_KEY, JSON.stringify([...seen.slice(-19), id]));
  } catch { /* storage blocked: Google still dedupes by transaction_id */ }
  return false;
}

// A paid booking. Sent once per booking, with the amount the server charged.
export function trackBookingPaid(receipt) {
  if (!loaded || !receipt) return;
  const id = String(receipt.bookingNumber || receipt.bookingId || '');
  if (!id || alreadyTracked(id)) return;
  const value = Number.isFinite(receipt.total) ? receipt.total : undefined;
  if (ADS_ID && BOOKING_LABEL) {
    send('event', 'conversion', { send_to: `${ADS_ID}/${BOOKING_LABEL}`, value, currency: 'USD', transaction_id: id });
  }
  send('event', 'purchase', {
    transaction_id: id,
    value,
    currency: 'USD',
    items: [{ item_name: receipt.serviceName || 'Ride', item_variant: receipt.vehicleName || undefined, price: value, quantity: 1 }],
  });
}
